import { useState } from "react";
import { api, API } from "@/lib/rn3";
import { toast } from "sonner";
import { X, Send, Loader2, Download } from "lucide-react";

/**
 * Reusable modal: preview + email a back-office document (invoice or estimate).
 * Props:
 *   - open, onClose
 *   - kind: "invoice" | "estimate"
 *   - bookId
 *   - doc: the document (must have id, invoice_number/estimate_number, total, customer_id)
 *   - defaultEmail: customer.email
 *   - lang
 *   - onSent: callback(updatedDoc) after email succeeds
 */
const SendDocModal = ({ open, onClose, kind, bookId, doc, defaultEmail, lang, onSent }) => {
  const [to, setTo] = useState(defaultEmail || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open || !doc) return null;
  const numberKey = kind === "invoice" ? "invoice_number" : "estimate_number";
  const titleEn = kind === "invoice" ? `Email Invoice ${doc[numberKey] || ""}` : `Email Estimate ${doc[numberKey] || ""}`;
  const titleEs = kind === "invoice" ? `Enviar Factura ${doc[numberKey] || ""}` : `Enviar Presupuesto ${doc[numberKey] || ""}`;

  const tokenQS = `?token=${encodeURIComponent(localStorage.getItem("rn3_token") || "")}`;
  const pdfPath = `${API}/back-office/books/${bookId}/${kind === "invoice" ? "invoices" : "estimates"}/${doc.id}/pdf`;

  const downloadPdf = async () => {
    try {
      const { data } = await api.get(`/back-office/books/${bookId}/${kind === "invoice" ? "invoices" : "estimates"}/${doc.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}-${doc[numberKey] || doc.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 4000);
    } catch (err) {
      toast.error("Could not download PDF");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!to) { toast.error(lang === "es" ? "Email requerido" : "Recipient email required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/back-office/books/${bookId}/${kind === "invoice" ? "invoices" : "estimates"}/${doc.id}/email`,
        { to, subject: subject || undefined, message: message || undefined },
      );
      if (data?.mocked) {
        toast.success(lang === "es" ? "Email enviado (modo prueba — sin clave Resend)" : "Email sent (MOCKED — no Resend key configured)");
      } else {
        toast.success(lang === "es" ? "Email enviado" : "Email sent");
      }
      onSent?.(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid={`send-${kind}-modal`}>
      <div className="bg-white max-w-lg w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
          <h3 className="font-serif-display text-2xl">{lang === "es" ? titleEs : titleEn}</h3>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Destinatario" : "To"}</label>
            <input required type="email" value={to} onChange={(e) => setTo(e.target.value)} className="rn3-input" data-testid={`send-${kind}-to`} placeholder="customer@example.com" />
          </div>
          <div>
            <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Asunto (opcional)" : "Subject (optional)"}</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="rn3-input" data-testid={`send-${kind}-subject`} placeholder={kind === "invoice" ? `Invoice ${doc[numberKey] || ""} from Roman Numerals III` : `Estimate ${doc[numberKey] || ""} from Roman Numerals III`} />
          </div>
          <div>
            <label className="rn3-eyebrow block mb-2">{lang === "es" ? "Mensaje (opcional)" : "Message (optional)"}</label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="rn3-input" data-testid={`send-${kind}-message`} placeholder={lang === "es" ? "Mensaje personalizado para el cliente…" : "Custom message to your customer…"} />
          </div>
          <div className="text-xs text-[#737373] border border-[#F3EFE9] bg-[#FDFBF7] px-3 py-2">
            {lang === "es" ? "Se adjuntará un PDF de la " : "A branded PDF of the "}
            {kind === "invoice" ? (lang === "es" ? "factura" : "invoice") : (lang === "es" ? "cotización" : "estimate")}
            {lang === "es" ? " con tu marca." : " will be attached."}
          </div>
          <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#E5E5E5]">
            <button type="button" onClick={downloadPdf} className="rn3-btn-gold" data-testid={`send-${kind}-download`}>
              <Download className="w-4 h-4" /> {lang === "es" ? "Descargar PDF" : "Download PDF"}
            </button>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onClose} className="text-sm text-[#737373] hover:text-[#0A0A0A] uppercase tracking-widest">{lang === "es" ? "Cancelar" : "Cancel"}</button>
              <button disabled={busy} className="rn3-btn-primary" data-testid={`send-${kind}-submit`}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {busy ? "…" : (lang === "es" ? "Enviar Email" : "Send Email")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendDocModal;
